import type * as React from "react";

import { cn } from "../lib/cn";

/**
 * `span` is phrasing content and the default, because the commonest home for
 * this row is inside a card's full-bleed `<button>`, where a `<div>` is invalid
 * HTML. `div` and `header` are for the rows that sit on a panel instead.
 */
export type CardHeaderRowAs = "span" | "div" | "header";

/**
 * `start` is the default: a two-line title and a one-line badge hang from the
 * same top edge. `baseline` is for ruled paper, where a chip has to sit on the
 * same rule as the word beside it rather than at the top of the line box.
 */
export type CardHeaderRowAlign = "start" | "baseline";

const alignItems: Record<CardHeaderRowAlign, string> = {
  start: "items-start",
  baseline: "items-baseline",
};

export interface CardHeaderRowProps extends React.HTMLAttributes<HTMLElement> {
  /** The element to render. `span` (default) is the phrasing-safe one. */
  as?: CardHeaderRowAs;
  /** How the title and the trailing slot line up. */
  align?: CardHeaderRowAlign;
  /**
   * The slot after the title: a badge, a stamp, a swipe, a severity chip.
   * Rendered unstyled inside a `shrink-0` cell - layout only, never a look.
   */
  trailing?: React.ReactNode;
  /**
   * Type for the title cell: the face, size and clamp are the card's business.
   * The cell's own `min-w-0 flex-1` is not, and is not overridable.
   */
  titleClassName?: string;
  /** What the card is about. */
  children: React.ReactNode;
  ref?: React.Ref<HTMLElement>;
}

/**
 * The line across the top of a card: what it is on the left, an optional badge
 * on the right.
 *
 * The whole reason this is a component is the three classes people leave out.
 * A flex child's `min-width` is `auto`, not `0`, so a title that cannot break -
 * a filename, a hostname, a long patient name - refuses to shrink below its
 * content and pushes the badge off the card, and neither `truncate` nor
 * `line-clamp-*` on the title does anything until `min-w-0` lets it. `flex-1`
 * is what makes the title claim the leftover width so the badge is pinned right
 * without `ml-auto`, and `shrink-0` keeps the badge from being squeezed into a
 * two-line sliver when the title is long. Getting one of the three wrong looks
 * fine until real data arrives.
 *
 * The title cell is always a `<span>`, so a header row is phrasing content
 * exactly when its host element is.
 */
export function CardHeaderRow({
  as = "span",
  align = "start",
  trailing,
  titleClassName,
  children,
  className,
  ...rest
}: CardHeaderRowProps) {
  /*
   * All three tags are plain HTMLElement boxes with no attributes of their own,
   * so one cast keeps the JSX readable instead of three near-identical returns.
   */
  const Row = as as "span";

  return (
    <Row
      className={cn("flex justify-between gap-2", alignItems[align], className)}
      {...rest}
    >
      <span className={cn("min-w-0 flex-1", titleClassName)}>{children}</span>
      {trailing != null && <span className="shrink-0">{trailing}</span>}
    </Row>
  );
}
